import React from 'react'
import { useTranslation } from 'react-i18next'
import { Box } from '@node-space/ui-components'
import { SidePanelSection } from 'components/SidePanel/SidePanelSection'
import {
  SidePanelSummary,
  type SidePanelRowProps,
  type SidePanelSummaryProps,
} from 'components/SidePanel/SidePanelSummary'
import type { IndividualApplicationDetails } from 'types/customers'
import { formatEnumValue } from '../utils/utils'

const IndividualDetailsSection = ({ individual }: { individual: IndividualApplicationDetails }) => {
  const { t } = useTranslation()
  const rows: SidePanelSummaryProps['rows'] = [
    { label: t('customers.firstName'), content: individual.firstName },
    { label: t('customers.lastName'), content: individual.lastName },
    { label: t('customers.dateOfBirth'), content: individual.dateOfBirth },
    { label: t('customers.emailAddress'), content: individual.emailAddress },
    { label: t('customers.phoneNumber'), content: individual.phoneNumber },
    { label: t('customers.nationality'), content: individual.nationality },
    { label: t('customers.placeOfBirth'), content: individual.placeOfBirth },
    { label: t('customers.taxNumber'), content: individual.taxIdentification?.number },
    {
      label: t('customers.taxResidency'),
      content: individual.taxIdentification?.taxResidenceCountryCode,
    },
  ].filter((row): row is SidePanelRowProps => !!row.content)
  if (!rows.length) return null
  return (
    <SidePanelSection heading={t('customers.individualDetails')}>
      <SidePanelSummary rows={rows} />
    </SidePanelSection>
  )
}

const IndividualAddressSection = ({ individual }: { individual: IndividualApplicationDetails }) => {
  const { t } = useTranslation()
  const { address } = individual
  if (!address) return null
  return (
    <SidePanelSection heading={t('customers.residentialAddress')}>
      <SidePanelSummary
        rows={[
          { label: t('customers.addressLine1'), content: address.addressLine1 },
          { label: t('customers.addressLine2'), content: address.addressLine2 },
          { label: t('customers.city'), content: address.city },
          { label: t('customers.postalCode'), content: address.postalCode },
          { label: t('customers.stateCode'), content: address.stateCode },
          { label: t('customers.countryCode'), content: address.countryCode },
        ].filter((row): row is SidePanelRowProps => !!row.content)}
      />
    </SidePanelSection>
  )
}

const IndividualCddSection = ({ individual }: { individual: IndividualApplicationDetails }) => {
  const { t } = useTranslation()
  const { cdd } = individual
  if (!cdd) return null
  return (
    <SidePanelSection heading={t('customers.cddDetails')}>
      <SidePanelSummary
        rows={[
          {
            label: t('customers.employmentStatus'),
            content: cdd.employmentStatus ? formatEnumValue(cdd.employmentStatus) : undefined,
          },
          {
            label: t('customers.sourceOfFunds'),
            content: cdd.sourceOfFunds ? formatEnumValue(cdd.sourceOfFunds) : undefined,
          },
          {
            label: t('customers.pepStatus'),
            content: cdd.pepStatus ? formatEnumValue(cdd.pepStatus) : undefined,
          },
          {
            label: t('customers.intendedUse'),
            content: cdd.intendedUseOfAccount
              ? formatEnumValue(cdd.intendedUseOfAccount)
              : undefined,
          },
          {
            label: t('customers.expectedMonthlyVolume'),
            content: cdd.expectedMonthlyVolume
              ? `${cdd.expectedMonthlyVolume.amount} ${cdd.expectedMonthlyVolume.currency}`
              : undefined,
          },
        ].filter((row): row is SidePanelRowProps => !!row.content)}
      />
    </SidePanelSection>
  )
}

export const CustomerIndividualTab = ({
  individual,
}: {
  individual: IndividualApplicationDetails
}) => (
  <Box flexCol gapY={16} padding={16}>
    <IndividualDetailsSection individual={individual} />
    <IndividualAddressSection individual={individual} />
    <IndividualCddSection individual={individual} />
  </Box>
)
